import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Cross } from '@asphalt-react/iconpack';
import { propsUtil, DANGER, WARNING, SUCCESS, INFO } from '@asphalt-react/helper';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Tag__PwmUk {\n  --tag-text-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --tag-background-color: var(--support-neutral-fill, hsla(220, 17%, 93%, 1));\n  --tag-background-color-danger: var(\n    --support-danger-fill,\n    hsla(14, 72%, 94%, 1)\n  );\n  --tag-background-color-info: var(--support-info-fill, hsla(210, 67%, 94%, 1));\n  --tag-background-color-success: var(\n    --support-success-fill,\n    hsla(157, 35%, 93%, 1)\n  );\n  --tag-background-color-warning: var(\n    --support-warning-fill,\n    hsla(33, 100%, 96%, 1)\n  );\n  --tag-background-active: var(\n    --interactive-fill-selected-active,\n    hsla(219, 42%, 24%, 1)\n  );\n  --tag-background-danger-active: var(\n    --support-danger-active,\n    hsla(12, 92%, 36%, 1)\n  );\n  --tag-background-warning-active: var(\n    --support-warning-active,\n    hsla(33, 79%, 45%, 1)\n  );\n  --tag-background-success-active: var(\n    --support-success-active,\n    hsla(159, 98%, 21%, 1)\n  );\n  --tag-background-info-active: var(\n    --support-info-active,\n    hsla(209, 100%, 34%, 1)\n  );\n  /* font-size */\n  --tag-m-size: var(--body-m, 1rem);\n  --tag-l-size: var(--body-l, 1.13rem);\n  --tag-s-size: var(--body-s, 0.88rem);\n\n  display: inline-flex;\n  justify-content: center;\n  align-items: center;\n  padding: 0 0.75rem;\n  color: var(--tag-text-color);\n  background: var(--tag-background-color);\n  border-radius: 25px;\n  font-size: var(--tag-font-size);\n}\n\n.danger__3CtCh {\n  background-color: var(--tag-background-color-danger);\n}\n\n.success__1pxr2 {\n  background-color: var(--tag-background-color-success);\n}\n\n.info__M13mI {\n  background-color: var(--tag-background-color-info);\n}\n\n.warning__3vGCi {\n  background-color: var(--tag-background-color-warning);\n}\n\n/* sizes of Tag */\n.m__38-Oi {\n  font-size: var(--tag-m-size);\n}\n\n.s__2ioUp {\n  font-size: var(--tag-s-size);\n}\n\n.l__1canm {\n  font-size: var(--tag-l-size);\n}\n\n/* padding for tags with dot and without icon */\n.withDot__Ni1cA.m__38-Oi {\n  padding: 0 0.75rem 0 0.4375rem;\n}\n\n.withDot__Ni1cA.s__2ioUp {\n  padding: 0 0.75rem 0 0.375rem;\n}\n\n.withDot__Ni1cA.l__1canm {\n  padding: 0 0.75rem 0 0.5rem;\n}\n\n/* padding for tag with icon and with/without dot */\n.withIcon__3LnS4,\n.withIcon__3LnS4.withDot__Ni1cA {\n  padding-right: 0;\n}\n\n/* styles for dot */\n.dot__rz5XU {\n  --background-color-danger: var(\n    --support-danger-default,\n    hsla(12, 92%, 45%, 1)\n  );\n  --background-color-warning: var(\n    --support-warning-default,\n    hsla(33, 100%, 56%, 1)\n  );\n  --background-color-success: var(\n    --support-success-default,\n    hsla(159, 97%, 27%, 1)\n  );\n  --background-color-info: var(--support-info-default, hsla(209, 100%, 42%, 1));\n\n  display: inline-block;\n  border-radius: 50%;\n}\n\n/* size of dot for different size of tags */\n.m__38-Oi .dot__rz5XU {\n  width: 0.625rem;\n  height: 0.625rem;\n  margin-right: 0.625rem;\n}\n\n.s__2ioUp .dot__rz5XU {\n  width: 0.5rem;\n  height: 0.5rem;\n  margin-right: 0.5rem;\n}\n\n.l__1canm .dot__rz5XU {\n  width: 0.75rem;\n  height: 0.75rem;\n  margin-right: 0.75rem;\n}\n\n.danger__3CtCh .dot__rz5XU {\n  background-color: var(--background-color-danger);\n}\n\n.warning__3vGCi .dot__rz5XU {\n  background-color: var(--background-color-warning);\n}\n\n.success__1pxr2 .dot__rz5XU {\n  background-color: var(--background-color-success);\n}\n\n.info__M13mI .dot__rz5XU {\n  background-color: var(--background-color-info);\n}\n\n/* styles for cross icon */\n.cross__fyeG9 {\n  --cross-focus-border-color: var(--interactive-focus, hsla(219, 42%, 18%, 1));\n  --cross-hover-background-color: var(\n    --interactive-fill-selected-hover,\n    hsla(219, 23%, 44%, 1)\n  );\n  --cross-hover-background-color-danger: var(\n    --support-danger-hover,\n    hsla(12, 76%, 56%, 1)\n  );\n  --cross-hover-background-color-warning: var(\n    --support-warning-hover,\n    hsla(33, 100%, 65%, 1)\n  );\n  --cross-hover-background-color-success: var(\n    --support-success-hover,\n    hsla(159, 51%, 41%, 1)\n  );\n  --cross-hover-background-color-info: var(\n    --support-info-hover,\n    hsla(209, 72%, 54%, 1)\n  );\n  --cross-hover-icon-color: var(--icon-inverse, hsla(0, 0%, 100%, 1));\n\n  background-color: inherit;\n  display: inline-flex;\n  align-self: stretch;\n  align-items: center;\n  border-radius: 0 25px 25px 0;\n  margin-left: 0.25rem;\n  border: 1px solid transparent;\n}\n\n/* \n* Pointer events none is added to stop event propagation from svg (or path) back to the user \n*/\n\n.cross__fyeG9 svg {\n  pointer-events: none;\n}\n\n/* padding for cross icon for different sizes */\n.m__38-Oi .cross__fyeG9 {\n  padding: 0 0.4375rem;\n}\n\n.l__1canm .cross__fyeG9 {\n  padding: 0 0.5rem;\n}\n\n.s__2ioUp .cross__fyeG9 {\n  padding: 0 0.375rem;\n}\n\n/* size of svg for different sizes */\n.s__2ioUp .cross__fyeG9 svg {\n  width: 12px;\n}\n\n.m__38-Oi .cross__fyeG9 svg {\n  width: 14px;\n}\n\n.l__1canm .cross__fyeG9 svg {\n  width: 16px;\n}\n\n.cross__fyeG9:focus {\n  outline: 0;\n  border-color: var(--cross-focus-border-color);\n}\n\n/* hover and focus styles */\n.cross__fyeG9:hover,\n.cross__fyeG9:focus {\n  background-color: var(--cross-hover-background-color, hsla(219, 23%, 44%, 1));\n}\n\n.danger__3CtCh .cross__fyeG9:hover,\n.danger__3CtCh .cross__fyeG9:focus {\n  background-color: var(--cross-hover-background-color-danger);\n}\n\n.warning__3vGCi .cross__fyeG9:hover,\n.warning__3vGCi .cross__fyeG9:focus {\n  background-color: var(--cross-hover-background-color-warning);\n}\n\n.success__1pxr2 .cross__fyeG9:hover,\n.success__1pxr2 .cross__fyeG9:focus {\n  background-color: var(--cross-hover-background-color-success);\n}\n\n.info__M13mI .cross__fyeG9:hover,\n.info__M13mI .cross__fyeG9:focus {\n  background-color: var(--cross-hover-background-color-info);\n}\n\n.cross__fyeG9:hover > svg path,\n.cross__fyeG9:focus > svg path {\n  fill: var(--cross-hover-icon-color);\n}\n\n.Tag__PwmUk .cross__fyeG9:active {\n  background-color: var(--tag-background-active);\n}\n\n.Tag__PwmUk.danger__3CtCh .cross__fyeG9:active {\n  background-color: var(--tag-background-danger-active);\n}\n\n.Tag__PwmUk.warning__3vGCi .cross__fyeG9:active {\n  background-color: var(--tag-background-warning-active);\n}\n\n.Tag__PwmUk.success__1pxr2 .cross__fyeG9:active {\n  background-color: var(--tag-background-success-active);\n}\n\n.Tag__PwmUk.info__M13mI .cross__fyeG9:active {\n  background-color: var(--tag-background-info-active);\n}\n\n/* dir=\"rtl\" styles */\n*[dir=\"rtl\"] .Tag__PwmUk {\n  padding: 0 0.75rem;\n}\n\n*[dir=\"rtl\"] .withDot__Ni1cA.m__38-Oi {\n  padding: 0 0.4375rem 0 0.75rem;\n}\n\n*[dir=\"rtl\"] .withDot__Ni1cA.s__2ioUp {\n  padding: 0 0.375rem 0 0.75rem;\n}\n\n*[dir=\"rtl\"] .withDot__Ni1cA.l__1canm {\n  padding: 0 0.5rem 0 0.75rem;\n}\n\n*[dir=\"rtl\"] .withIcon__3LnS4,\n*[dir=\"rtl\"] .withIcon__3LnS4.withDot__Ni1cA {\n  padding-left: 0;\n}\n\n*[dir=\"rtl\"] .cross__fyeG9 {\n  margin-right: 0.25rem;\n  margin-left: 0;\n  border-radius: 25px 0 0 25px;\n}\n\n*[dir=\"rtl\"] .m__38-Oi .dot__rz5XU {\n  margin-right: 0;\n  margin-left: 0.625rem;\n}\n\n*[dir=\"rtl\"] .s__2ioUp .dot__rz5XU {\n  margin-right: 0;\n  margin-left: 0.5rem;\n}\n\n*[dir=\"rtl\"] .l__1canm .dot__rz5XU {\n  margin-right: 0;\n  margin-left: 0.75rem;\n}\n";
var styles = {"Tag":"Tag__PwmUk","danger":"danger__3CtCh","success":"success__1pxr2","info":"info__M13mI","warning":"warning__3vGCi","m":"m__38-Oi","s":"s__2ioUp","l":"l__1canm","withDot":"withDot__Ni1cA","withIcon":"withIcon__3LnS4","dot":"dot__rz5XU","cross":"cross__fyeG9"};
styleInject(css_248z);

var createPropOccurValidator = propsUtil.createPropOccurValidator;
var typePropValidator = createPropOccurValidator([DANGER, WARNING, SUCCESS, INFO]);
var getTypeClass = function getTypeClass(type) {
  var isTypeInvalid = !typePropValidator(type);

  if (isTypeInvalid) {
    return "";
  }

  var info = type.info,
      success = type.success,
      warning = type.warning,
      danger = type.danger;

  if (danger) {
    return styles.danger;
  }

  if (info) {
    return styles.info;
  }

  if (success) {
    return styles.success;
  }

  if (warning) {
    return styles.warning;
  }

  return "";
};
var sizeClass = function sizeClass(size) {
  switch (size) {
    case "l":
      return styles.l;

    case "m":
      return styles.m;

    case "s":
      return styles.s;

    default:
      return styles.m;
  }
};

var Tag = function Tag(_ref) {
  var children = _ref.children,
      size = _ref.size,
      danger = _ref.danger,
      success = _ref.success,
      warning = _ref.warning,
      info = _ref.info,
      dismissible = _ref.dismissible,
      onDismiss = _ref.onDismiss,
      style = _ref.style,
      rest = _objectWithoutProperties(_ref, ["children", "size", "danger", "success", "warning", "info", "dismissible", "onDismiss", "style"]);

  var showDot = !!getTypeClass({
    danger: danger,
    info: info,
    success: success,
    warning: warning
  });
  var tagClass = cn(styles.Tag, sizeClass(size), getTypeClass({
    danger: danger,
    info: info,
    success: success,
    warning: warning
  }), _defineProperty({}, styles.withIcon, dismissible), _defineProperty({}, styles.withDot, showDot));
  return /*#__PURE__*/React.createElement("span", _extends({}, rest, {
    className: tagClass
  }), showDot && /*#__PURE__*/React.createElement("span", {
    className: styles.dot
  }), children, dismissible && /*#__PURE__*/React.createElement("button", {
    className: styles.cross,
    onClick: onDismiss,
    type: "button"
  }, /*#__PURE__*/React.createElement(Cross, null)));
};
Tag.propTypes = {
  /**
   * Keyword or label for the tag
   */
  children: PropTypes.string.isRequired,

  /**
   * Controls size of the tag. Accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Applies danger support look
   */
  danger: PropTypes.bool,

  /**
   * Applies warning support look
   */
  warning: PropTypes.bool,

  /**
   * Applies information support look
   */
  info: PropTypes.bool,

  /**
   * Applies success support look
   */
  success: PropTypes.bool,

  /**
   * Renders tag with an interactive cross icon
   */
  dismissible: PropTypes.bool,

  /**
   * Function to be called when tag is dismissed
   *
   * _React's synthetic [event](https://reactjs.org/docs/events.html) is available as an argument_
   *
   * ```
   *  ({targetsEvent}) => console.log(targetsEvent)
   * ```
   */
  onDismiss: PropTypes.func
};
Tag.defaultProps = {
  size: "m",
  danger: false,
  warning: false,
  info: false,
  success: false,
  dismissible: false
};

export default Tag;
